import { create } from "zustand";

// ─── Scan-started signal ───
//
// The header's ScanButton starts a brand-visibility scan, but the
// scan-progress UI lives on the visibility pages (brand, competitors,
// prompts), which only probe /api/visibility/active-workflow on mount.
// Without a signal the progress bar stayed hidden until a reload.
//
// Whoever starts a scan calls `announceScanStarted` with the workflow
// id from POST /api/ai-responses/check; mounted pages subscribe (via
// `useScanSignalStore.subscribe`, so only *new* announcements fire,
// not a stale one left over from an earlier page) and start polling.

export interface StartedScan {
  projectId: string;
  workflowId: string;
  totalChecks: number;
}

interface ScanSignalState {
  lastStartedScan: StartedScan | null;
  announceScanStarted: (scan: StartedScan) => void;
}

export const useScanSignalStore = create<ScanSignalState>()((set) => ({
  lastStartedScan: null,
  announceScanStarted: (scan) => set({ lastStartedScan: scan }),
}));

/**
 * Calls `onStart` whenever a scan is announced for `projectId` after
 * the subscription is set up. Returns the unsubscribe function, so it
 * can be returned straight from a `useEffect`.
 */
export function subscribeToScanStarts(
  projectId: string,
  onStart: (scan: StartedScan) => void,
): () => void {
  return useScanSignalStore.subscribe((state, prev) => {
    const scan = state.lastStartedScan;
    if (!scan || scan === prev.lastStartedScan) return;
    if (scan.projectId !== projectId) return;
    onStart(scan);
  });
}
